// Copy for the RBC RightPay onboarding page (/rbc).
//
// The audience is two groups at once: cardholders moving an existing RBC RightPay card over
// from the previous processor, and cardholders holding a newly issued card from the same
// programme. Wording must suit both without asking which they are. These cardholders did not
// choose to move, so the copy explains rather than instructs.
//
// Field-level validation and API error messages reuse the shared `errors-*` keys in auth.js,
// which already exist in both languages.

export const rbcEn = {
  'rbc-header-help': 'Help',
  'rbc-step-counter': 'Step {current} of {total}',

  // Step 1 - Intro
  //
  // Migration notice (DH-2772). RBC asked for this on the landing step only: it describes
  // what a cardholder meets *after* setting up here, so it has no place on the later steps.
  // English wording is RBC's own and is reproduced verbatim -- do not reword it.
  'rbc-intro-notice-heading': 'Important Update:',
  'rbc-intro-notice-body':
    'Starting November 13, 2026, you will need to set up a new password the first time you log in to the consumer website. Please follow the prompts to create your new password for enhanced security. Please also note that alerts will be unavailable until further notice. If you encounter any issues, contact our support team for assistance. Thank you for your cooperation.',

  'rbc-intro-title': 'Set up your card account',
  'rbc-intro-subtitle':
    'Your RBC RightPay card is now supported by Berkeley Payment Solutions. Set up your account here to check your balance, view transactions and manage your card.',
  'rbc-intro-what-heading': 'What’s changing',
  'rbc-intro-what-body':
    'Your card keeps working as it always has. Your card number, balance and PIN do not change. You only need to create a new username and password for the portal where you manage it.',
  'rbc-intro-need-heading': 'What you’ll need',
  'rbc-intro-need-card': 'Your RBC RightPay card in hand',
  'rbc-intro-need-email': 'An email address you can access',
  'rbc-intro-time': 'This takes about three minutes.',
  'rbc-intro-continue': 'Get started',
  'rbc-intro-have-account': 'Already set up your account? Sign in',

  // Step 2 - Confirm your card
  'rbc-card-title': 'Confirm your card',
  'rbc-card-subtitle':
    'Enter the details exactly as they appear on your card, so we can confirm the card is yours.',
  'rbc-card-cvv-hint': 'The {length, number} digits on the back of your card',
  'rbc-card-submit': 'Continue',
  'rbc-card-back': 'Back',
  'rbc-card-already-registered':
    'This card already has an account. Please sign in with the username and password you created.',

  // Step 3 - Activate (only shown when the card is not yet activated)
  'rbc-activate-title': 'Activate your card first',
  'rbc-activate-subtitle':
    'This card has not been activated yet. That is normal for a newly issued card. Activate it here and you can carry straight on with setting up your account.',
  'rbc-activate-card-summary': 'Card ending in {lastFour}',
  'rbc-activate-no-reentry': 'You do not need to enter your card details again.',
  'rbc-activate-captcha-note':
    'Please confirm you are not a robot once more so we can finish activating this card.',
  'rbc-activate-submit': 'Activate and continue',

  // Step 4 - Set up your login
  'rbc-login-title': 'Create your login',
  'rbc-login-subtitle':
    'Choose a username and password. You will use these every time you sign in.',
  'rbc-login-username-label': 'Username',
  'rbc-login-password-label': 'Password',
  'rbc-login-passwordconfirm-label': 'Confirm password',
  'rbc-login-email-label': 'Email address',
  'rbc-login-email-hint': 'We use this to confirm your account is ready.',
  'rbc-login-submit': 'Create my account',
  'rbc-login-username-hint': '3 to 30 characters. Letters, numbers and . , ? @ & ! # \' ~ * - ; +',
  'rbc-password-rules-heading': 'Your password needs:',
  'rbc-password-rule-lowercase': 'Must have one lowercase letter',
  'rbc-show-password': 'Show password',
  'rbc-hide-password': 'Hide password',

  // Step 5 - Done
  'rbc-done-title': 'Your account is ready',
  'rbc-done-body':
    'Sign in with the username and password you just created to see your balance and transactions.',
  'rbc-done-security-note':
    'For your security we have not signed you in automatically. Signing in yourself confirms your new password works.',
  'rbc-done-signin': 'Go to sign in',
  'rbc-done-finishing': 'Finishing setting your PIN…',

  // Optional PIN step. Heading, body, labels and errors reuse the register-set-pin-* messages.
  'rbc-pin-submit': 'Set my PIN',
  'rbc-pin-galileo-submit': 'Continue to the secure page',

  // Errors
  'rbc-error-generic': 'Something went wrong. Please try again.',
  'rbc-error-activation-failed':
    'We could not activate your card. Please try again, or contact support if this keeps happening.',
};

export const rbcFr = {
  'rbc-header-help': 'Aide',
  'rbc-step-counter': 'Étape {current} sur {total}',

  // Étape 1 - Introduction
  //
  // Migration notice (DH-2772). This French wording is a Berkeley draft standing in until RBC
  // returns their own translation; the English above is RBC-approved, this is not. Replace it
  // wholesale when the approved copy arrives rather than editing it line by line.
  'rbc-intro-notice-heading': 'Mise à jour importante :',
  'rbc-intro-notice-body':
    'À compter du 13 novembre 2026, vous devrez créer un nouveau mot de passe lors de votre première connexion au site Web des consommateurs. Veuillez suivre les instructions afin de créer votre nouveau mot de passe pour une sécurité accrue. Veuillez également noter que les alertes seront indisponibles jusqu’à nouvel ordre. Si vous éprouvez des difficultés, communiquez avec notre équipe de soutien. Merci de votre collaboration.',

  'rbc-intro-title': 'Configurez votre compte de carte',
  'rbc-intro-subtitle':
    'Votre carte RBC RightPay est désormais prise en charge par Berkeley Payment Solutions. Configurez votre compte ici pour consulter votre solde, voir vos transactions et gérer votre carte.',
  'rbc-intro-what-heading': 'Ce qui change',
  'rbc-intro-what-body':
    'Votre carte continue de fonctionner comme avant. Votre numéro de carte, votre solde et votre NIP ne changent pas. Vous devez seulement créer un nouveau nom d’utilisateur et un mot de passe pour le portail où vous la gérez.',
  'rbc-intro-need-heading': 'Ce dont vous avez besoin',
  'rbc-intro-need-card': 'Votre carte RBC RightPay en main',
  'rbc-intro-need-email': 'Une adresse courriel à laquelle vous avez accès',
  'rbc-intro-time': 'Cela prend environ trois minutes.',
  'rbc-intro-continue': 'Commencer',
  'rbc-intro-have-account': 'Compte déjà configuré? Connectez-vous',

  // Étape 2 - Confirmez votre carte
  'rbc-card-title': 'Confirmez votre carte',
  'rbc-card-subtitle':
    'Entrez les renseignements exactement comme ils figurent sur votre carte, afin que nous puissions confirmer qu’elle vous appartient.',
  'rbc-card-cvv-hint': 'Les {length, number} chiffres au verso de votre carte',
  'rbc-card-submit': 'Continuer',
  'rbc-card-back': 'Retour',
  'rbc-card-already-registered':
    'Cette carte possède déjà un compte. Veuillez vous connecter avec le nom d’utilisateur et le mot de passe que vous avez créés.',

  // Étape 3 - Activation (affichée uniquement si la carte n’est pas encore activée)
  'rbc-activate-title': 'Activez d’abord votre carte',
  'rbc-activate-subtitle':
    'Cette carte n’a pas encore été activée. C’est normal pour une carte nouvellement émise. Activez-la ici et vous pourrez poursuivre directement la configuration de votre compte.',
  'rbc-activate-card-summary': 'Carte se terminant par {lastFour}',
  'rbc-activate-no-reentry':
    'Vous n’avez pas besoin d’entrer de nouveau les renseignements de votre carte.',
  'rbc-activate-captcha-note':
    'Veuillez confirmer une fois de plus que vous n’êtes pas un robot afin que nous puissions terminer l’activation de cette carte.',
  'rbc-activate-submit': 'Activer et continuer',

  // Étape 4 - Créez vos identifiants
  'rbc-login-title': 'Créez vos identifiants',
  'rbc-login-subtitle':
    'Choisissez un nom d’utilisateur et un mot de passe. Vous les utiliserez chaque fois que vous vous connecterez.',
  'rbc-login-username-label': 'Nom d’utilisateur',
  'rbc-login-password-label': 'Mot de passe',
  'rbc-login-passwordconfirm-label': 'Confirmez le mot de passe',
  'rbc-login-email-label': 'Adresse courriel',
  'rbc-login-email-hint': 'Nous l’utilisons pour confirmer que votre compte est prêt.',
  'rbc-login-submit': 'Créer mon compte',
  'rbc-login-username-hint':
    'De 3 à 30 caractères. Lettres, chiffres et . , ? @ & ! # \' ~ * - ; +',
  'rbc-password-rules-heading': 'Votre mot de passe doit contenir :',
  'rbc-password-rule-lowercase': 'Doit contenir une lettre minuscule',
  'rbc-show-password': 'Afficher le mot de passe',
  'rbc-hide-password': 'Masquer le mot de passe',

  // Étape 5 - Terminé
  'rbc-done-title': 'Votre compte est prêt',
  'rbc-done-body':
    'Connectez-vous avec le nom d’utilisateur et le mot de passe que vous venez de créer pour consulter votre solde et vos transactions.',
  'rbc-done-security-note':
    'Pour votre sécurité, nous ne vous avons pas connecté automatiquement. Vous connecter vous-même confirme que votre nouveau mot de passe fonctionne.',
  'rbc-done-signin': 'Aller à la connexion',
  'rbc-done-finishing': 'Finalisation de votre NIP…',

  // Étape NIP facultative. Le titre, le texte, les libellés et les erreurs réutilisent les
  // messages register-set-pin-*.
  'rbc-pin-submit': 'Définir mon NIP',
  'rbc-pin-galileo-submit': 'Continuer vers la page sécurisée',

  // Erreurs
  'rbc-error-generic': 'Une erreur s’est produite. Veuillez réessayer.',
  'rbc-error-activation-failed':
    'Nous n’avons pas pu activer votre carte. Veuillez réessayer ou communiquer avec le soutien si le problème persiste.',
};
